import React from "react";
import { Download, Upload } from "lucide-react";
import { AppModal, AppModalHeader } from "../../../components/AppModal";
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS } from "../../../ui/controlStyles";
import { OfferImportSourcePicker, type OfferImportSource } from "./OfferImportSourcePicker";

const GAEB_VERSION_DEFAULT = "GAEB DA XML";
const GAEB_DP_OPTIONS = [
  { value: "X80", label: "LV-Katalog" },
  { value: "X81", label: "Leistungsverzeichnis" },
  { value: "X82", label: "Kostenvoranschlag" },
  { value: "X83", label: "Angebotsaufforderung" },
  { value: "X84", label: "Angebotsabgabe" },
  { value: "X85", label: "Nebenangebote" },
  { value: "X86", label: "Zuschlag/Auftragserteilung" },
] as const;

type Mode = "chooser" | "import" | "export";

type GaebActionButtonProps = {
  offeringId: string;
  onImported?: () => void | Promise<void>;
  className?: string;
  label?: React.ReactNode;
  /** Issued documents: export only, no position import. */
  exportOnly?: boolean;
  /** Aus dem Import-Menü: direkt den Import zeigen, ohne Auswahl Import/Export. */
  importOnly?: boolean;
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const GaebActionButton: React.FC<GaebActionButtonProps> = ({
  offeringId,
  onImported,
  className,
  label = "GAEB",
  exportOnly = false,
  importOnly = false,
  hideTrigger = false,
  open: openProp,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [mode, setMode] = React.useState<Mode>("chooser");
  const [version, setVersion] = React.useState(GAEB_VERSION_DEFAULT);
  const [dp, setDp] = React.useState<(typeof GAEB_DP_OPTIONS)[number]["value"]>("X80");
  const [includePrices, setIncludePrices] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busySource, setBusySource] = React.useState<OfferImportSource | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const close = () => {
    if (busy) return;
    setOpen(false);
    setMode("chooser");
    setError(null);
    setIncludePrices(true);
  };

  React.useEffect(() => {
    if (!open) return;
    setMode(exportOnly ? "export" : importOnly ? "import" : "chooser");
    setError(null);
  }, [open, exportOnly, importOnly, hideTrigger]);

  const runImport = async (input: File | OfferImportSource) => {
    setBusy(true);
    setError(null);
    if (!(input instanceof File)) setBusySource(input);
    try {
      let res: Response;
      if (input instanceof File) {
        const fd = new FormData();
        fd.append("file", input);
        res = await fetch(`/api/gaeb/offerings/${encodeURIComponent(offeringId)}/import`, {
          method: "POST",
          body: fd,
        });
      } else {
        res = await fetch(`/api/gaeb/offerings/${encodeURIComponent(offeringId)}/import-from`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source: input }),
        });
      }
      const raw = await res.text();
      let payload: { error?: string } = {};
      try {
        payload = raw ? (JSON.parse(raw) as { error?: string }) : {};
      } catch {
        payload = {};
      }
      if (!res.ok) {
        const detail = payload.error || raw.trim();
        throw new Error(detail || `GAEB-Import fehlgeschlagen (${res.status})`);
      }
      await onImported?.();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "GAEB-Import fehlgeschlagen.");
    } finally {
      setBusy(false);
      setBusySource(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const runExport = async () => {
    setBusy(true);
    setError(null);
    try {
      const url = `/api/gaeb/offerings/${encodeURIComponent(offeringId)}/export?version=${encodeURIComponent(version)}&dp=${encodeURIComponent(dp)}&include_prices=${includePrices ? "1" : "0"}`;
      const res = await fetch(url);
      if (!res.ok) {
        const payload = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || "GAEB-Export fehlgeschlagen.");
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || `angebot.${dp.toLowerCase()}`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "GAEB-Export fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {hideTrigger ? null : (
      <button
        type="button"
        className={className}
        onClick={() => {
          setOpen(true);
          setMode(exportOnly ? "export" : importOnly ? "import" : "chooser");
          setError(null);
        }}
      >
        {label}
      </button>
      )}

      <AppModal
        open={open}
        onClose={close}
        disableClose={busy}
        overlayClassName="z-[260]"
        panelClassName="md:max-w-lg"
        ariaLabelledBy="gaeb-dialog-title"
      >
        <AppModalHeader
          titleId="gaeb-dialog-title"
          title={mode === "chooser" ? "GAEB" : mode === "import" ? "GAEB-Datei importieren" : "Als GAEB exportieren"}
          onClose={close}
          closeDisabled={busy}
        />

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {mode === "chooser" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode("import")}
                className="w-full px-4 py-3 rounded-lg border border-app-border bg-app-surface text-left hover:bg-app-hover hover:border-app-border-strong transition-colors"
              >
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-app-text">
                  <Upload className="w-4 h-4 text-pipedrive-blue" />
                  Aus GAEB importieren
                </span>
                <p className="mt-1 text-xs text-app-text-subtle">Datei laden und Positionen in das Angebot übernehmen.</p>
              </button>
              <button
                type="button"
                onClick={() => setMode("export")}
                className="w-full px-4 py-3 rounded-lg border border-app-border bg-app-surface text-left hover:bg-app-hover hover:border-app-border-strong transition-colors"
              >
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-app-text">
                  <Download className="w-4 h-4 text-pipedrive-blue" />
                  Als GAEB exportieren
                </span>
                <p className="mt-1 text-xs text-app-text-subtle">Bestehende Positionen als GAEB-Datei herunterladen.</p>
              </button>
            </div>
          ) : (
            <>
              {mode === "import" ? (
                <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full text-left rounded-xl border border-dashed border-sky-200 bg-app-surface-muted/60 px-4 py-5 hover:bg-sky-50/50 hover:border-sky-300 transition-colors disabled:opacity-60 disabled:pointer-events-none"
                >
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
                      <Upload className="w-5 h-5" />
                    </div>
                    <p className="text-sm text-app-text-muted">
                      Datei hier auswählen oder{" "}
                      <span className="text-sky-700 font-semibold underline underline-offset-2">Datei wählen</span>
                    </p>
                    <p className="text-xs text-app-text-subtle">
                      Erlaubt: .x80, .x81, .x82, .x83, .x84, .x85, .x86, .xml (Max. 20 MB)
                    </p>
                  </div>
                </button>
                <OfferImportSourcePicker
                  offeringId={offeringId}
                  kind="gaeb"
                  disabled={busy}
                  busySource={busySource}
                  onPick={(source) => void runImport(source)}
                />
                </>
              ) : (
                <>
                  <p className="text-sm text-app-text-muted">
                    Format: <span className="font-medium text-app-text">{version}</span>
                  </p>

                  <fieldset className="space-y-1.5">
                    <legend className="mb-1.5 text-xs font-semibold text-app-text-subtle">Datenaustauschphase</legend>
                    {GAEB_DP_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex cursor-pointer items-center gap-2 text-sm text-app-text-muted">
                        <input
                          type="radio"
                          name="gaeb-dp"
                          checked={dp === opt.value}
                          onChange={() => setDp(opt.value)}
                        />
                        <span className="font-semibold text-app-text">{opt.value}</span>
                        <span className="text-app-text-subtle">{opt.label}</span>
                      </label>
                    ))}
                  </fieldset>

                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={includePrices}
                      onChange={(e) => setIncludePrices(e.target.checked)}
                      className="mt-0.5 shrink-0"
                    />
                    <span className="text-sm text-app-text-muted">
                      Preise exportieren
                      <span className="block text-xs text-app-text-subtle">
                        Wenn deaktiviert, werden Einheitspreis und Gesamtpreis leer exportiert.
                      </span>
                    </span>
                  </label>
                </>
              )}
            </>
          )}

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".x80,.x81,.x82,.x83,.x84,.x85,.x86,.xml,text/xml,application/xml"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void runImport(f);
          }}
        />

        {mode !== "import" ? (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-app-border px-5 py-3">
            <button
              type="button"
              onClick={() => {
                if (mode === "chooser" || exportOnly) close();
                else setMode("chooser");
              }}
              disabled={busy}
              className={APP_BTN_OUTLINE_CLASS}
            >
              Abbrechen
            </button>

            {mode === "export" ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void runExport()}
              className={APP_BTN_PRIMARY_CLASS}
            >
              {busy ? "Erstelle…" : `${dp} herunterladen`}
            </button>
            ) : null}
          </div>
        ) : null}
      </AppModal>
    </>
  );
};
